import React, { useState } from "react";
import styled from "@emotion/styled";
import { Outlet } from "react-router-dom";

import { Alert, Box, CssBaseline, Paper as MuiPaper } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { spacing } from "@mui/system";

import GlobalStyle from "../components/GlobalStyle";
import Navbar from "../components/navbar/Navbar";
import dashboardItems from "../components/sidebar/dashboardItems";
import Sidebar from "../components/sidebar/Sidebar";
import Footer from "../components/Footer";
import { useFilteredNavItems } from "../hooks/useFilteredNavItems";
import PropertyCandidatesDialog from "../components/propertyCandidates/PropertyCandidatesDialog";
import useAuth from "../hooks/useAuth";

const drawerWidth = 258;

const Root = styled.div`
  display: flex;
  min-height: 100vh;

  @media print {
    display: block;

    & .no-print {
      display: none !important;
    }
  }
`;

const Drawer = styled.div`
  ${(props) => props.theme.breakpoints.up("md")} {
    width: ${drawerWidth}px;
    flex-shrink: 0;
  }

  @media print {
    display: none !important;
  }
`;

const AppContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  max-width: 100%;
`;

const Paper = styled(MuiPaper)(spacing);

const MainContent = styled(Paper)`
  flex: 1;
  background: ${(props) => props.theme.palette.background.default};

  @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
    flex: none;
  }

  .MuiPaper-root .MuiPaper-root {
    box-shadow: none;
  }

  @media print {
    padding: 0 !important;
    margin: 0 !important;
    box-shadow: none !important;
    background: white !important;
  }
`;

interface DashboardType {
  children?: React.ReactNode;
}

const Dashboard: React.FC<DashboardType> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { currentUser } = useAuth();
  const hasNoActiveAssociations =
    currentUser &&
    (!currentUser.associations || currentUser.associations.length === 0);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const theme = useTheme();
  const isLgUp = useMediaQuery(theme.breakpoints.up("lg"));

  // Filter navigation items based on user permissions
  const { filteredItems } = useFilteredNavItems(dashboardItems);

  return (
    <Root>
      <CssBaseline />
      <GlobalStyle />
      <Drawer>
        <Box sx={{ display: { xs: "block", lg: "none" } }}>
          <Sidebar
            PaperProps={{ style: { width: drawerWidth } }}
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            items={filteredItems}
          />
        </Box>
        <Box sx={{ display: { xs: "none", md: "block" } }}>
          <Sidebar
            PaperProps={{ style: { width: drawerWidth } }}
            items={filteredItems}
          />
        </Box>
      </Drawer>
      <AppContent>
        <div className="no-print">
          <Navbar onDrawerToggle={handleDrawerToggle} />
        </div>
        <MainContent p={isLgUp ? 12 : 5}>
          <PropertyCandidatesDialog />
          {hasNoActiveAssociations && (
            <Alert severity="warning" sx={{ mb: 4 }}>
              Sinulla ei ole aktiivisia jäsenyyksiä tiehoitokunnissa. Ota
              yhteyttä tiehoitokunnan hallintajäseneen, joka voi aktivoida
              tunnuksesi.
            </Alert>
          )}
          {children}
          <Outlet />
        </MainContent>
        <div className="no-print">
          <Footer />
        </div>
      </AppContent>
    </Root>
  );
};

export default Dashboard;
